/**
 * The matter: every World's bits, per position key and plane, as the server
 * last sealed them. The only ways in are a `planes` snapshot and journal
 * commits applied strictly in chain order (seq + 1, prevHash = head), so what
 * is drawn is always a state the World really passed through. Scrubbing runs
 * the same commits backward. Nothing here guesses; nothing here fetches. Pure.
 *
 * A plane is `occupierId|role`, never a name: event and series names repeat.
 */

import type { CreatePlaneStore, Flip, FlipCause, JournalCommit, PlaneId, PlaneStore, PlanesAnswer, PositionMatter, Role, Window, WorldMatter } from '../contracts.ts';
import { full, hex, maskOf } from './masks.ts';

export type OccupierKind = 'holder' | 'event' | 'series' | 'foreign';

/** The store, plus a lookup its tests use: the key of a position in the window, if it is in it. */
export interface PlaneModel extends PlaneStore {
  keyAt(world: string, parametric: number, coordinates: readonly number[]): number | undefined;
}

export const planeId = (occupierId: number, role: Role): PlaneId => `${occupierId}|${role}`;

export const planeParts = (plane: PlaneId): { occupierId: number; role: Role } => {
  const [id, role] = plane.split('|');
  return { occupierId: Number(id), role: role as Role };
};

/** Commits that rewrite a hand's copy: every plane they touch is a foreign record. */
const COPY_KINDS = new Set(['takeIn', 'reconcile']);

/**
 * What is free at a position, from its planes alone: nothing held firmly or
 * softly, except by copies of other Worlds; a proposal holds nothing. The
 * same rule as `World.offered`, so it equals the server's answer.
 */
export const deriveOffered = (
  planes: ReadonlyMap<PlaneId, bigint>,
  kinds: ReadonlyMap<number, OccupierKind>,
  bits: number,
): bigint => {
  let taken = 0n;
  for (const [plane, mask] of planes) {
    const { occupierId, role } = planeParts(plane);
    if (role === 'proposed' || kinds.get(occupierId) === 'foreign') continue;
    taken |= mask;
  }
  return full(bits) & ~taken;
};

const positionKey = (parametric: number, coordinates: readonly number[]): string => `${parametric}|${coordinates.join(',')}`;

/** A deep copy, so scrubbing and snapshots never touch what is drawn. */
export const cloneMatter = (m: WorldMatter): WorldMatter => ({
  world: m.world,
  global: m.global,
  leafSlotBits: m.leafSlotBits,
  seats: m.seats,
  seq: m.seq,
  head: m.head,
  window: m.window,
  byKey: new Map([...m.byKey].map(([k, p]) => [k, { ...p, planes: new Map(p.planes) }])),
  keyOrder: [...m.keyOrder],
  kinds: new Map(m.kinds),
});

/**
 * Matter written back as the `planes` read that would give it: how a read
 * carried forward by commits off to the side is handed to the store. Names
 * are left empty; the store keys planes by occupier id and never reads them.
 */
export const answerOf = (m: WorldMatter): PlanesAnswer => ({
  seq: m.seq,
  head: m.head,
  leafSlotBits: m.leafSlotBits,
  seats: m.seats,
  positions: m.keyOrder.map((key) => {
    const pos = m.byKey.get(key)!;
    return {
      parametric: pos.parametric,
      coordinates: [...pos.coordinates],
      key,
      offered: hex(pos.offered),
      planes: [...pos.planes].map(([plane, mask]) => {
        const { occupierId, role } = planeParts(plane);
        return { occupier: '', id: occupierId, kind: m.kinds.get(occupierId) ?? 'holder', role, mask: hex(mask) };
      }),
    };
  }),
});

const matterFrom = (world: string, global: string, answer: PlanesAnswer, window: Window, kinds: Map<number, OccupierKind>): WorldMatter => {
  const byKey = new Map<number, PositionMatter>();
  for (const pos of answer.positions) {
    const planes = new Map<PlaneId, bigint>();
    for (const pl of pos.planes) {
      kinds.set(pl.id, pl.kind);
      const mask = maskOf(pl.mask);
      if (mask !== 0n) planes.set(planeId(pl.id, pl.role), mask);
    }
    byKey.set(pos.key, { key: pos.key, parametric: pos.parametric, coordinates: [...pos.coordinates], offered: maskOf(pos.offered), planes });
  }
  return {
    world, global, leafSlotBits: answer.leafSlotBits, seats: answer.seats, seq: answer.seq, head: answer.head,
    window, byKey, keyOrder: answer.positions.map((p) => p.key), kinds,
  };
};

/** Apply one commit's entries forward to matter (in place); flips for what changed inside the window. */
const forward = (m: WorldMatter, c: JournalCommit): Flip[] => {
  const cause: FlipCause = { kind: 'commit', seq: c.seq, hash: c.hash, commitKind: c.kind, actor: c.actor };
  const flips: Flip[] = [];
  const touched = new Set<number>();
  for (const e of c.entries) {
    if (!m.kinds.has(e.occupierId)) m.kinds.set(e.occupierId, COPY_KINDS.has(c.kind) && e.role === 'held' ? 'foreign' : guessKind(e.occupier));
    const pos = m.byKey.get(e.key);
    if (!pos) continue;
    const plane = planeId(e.occupierId, e.role);
    const was = pos.planes.get(plane) ?? 0n;
    const now = (was & ~maskOf(e.clear)) | maskOf(e.set);
    if (now === was) continue;
    if (now === 0n) pos.planes.delete(plane);
    else pos.planes.set(plane, now);
    flips.push({ world: m.world, key: e.key, plane, set: now & ~was, clear: was & ~now, cause });
    touched.add(e.key);
  }
  for (const key of touched) {
    const pos = m.byKey.get(key)!;
    pos.offered = deriveOffered(pos.planes, m.kinds, m.leafSlotBits);
  }
  m.seq = c.seq;
  m.head = c.hash;
  return flips;
};

/**
 * A kind for an occupier first met in a commit. Only copies matter to what is
 * offered, and copies are only written by take-in commits (handled above), so
 * anything else counts; a later `listOccupiers` or snapshot names it exactly.
 */
const guessKind = (name: string): OccupierKind => (/\/[0-9a-f]{8}$/.test(name) ? 'event' : 'holder');

/** Apply one commit backward (in place): the matter as it stood before it. */
const backward = (m: WorldMatter, c: JournalCommit): void => {
  const touched = new Set<number>();
  // One commit may touch a plane at a key twice (a copy cleared, then set): undo in reverse.
  for (const e of [...c.entries].reverse()) {
    const pos = m.byKey.get(e.key);
    if (!pos) continue;
    const plane = planeId(e.occupierId, e.role);
    const was = pos.planes.get(plane) ?? 0n;
    const before = (was & ~maskOf(e.set)) | maskOf(e.clear);
    if (before === 0n) pos.planes.delete(plane);
    else pos.planes.set(plane, before);
    touched.add(e.key);
  }
  for (const key of touched) {
    const pos = m.byKey.get(key)!;
    pos.offered = deriveOffered(pos.planes, m.kinds, m.leafSlotBits);
  }
  m.seq = c.seq - 1;
  m.head = c.prevHash;
};

/** Flips that turn `before` (what was shown) into `after`, key by key. */
const diff = (world: string, before: WorldMatter | undefined, after: WorldMatter): Flip[] => {
  const flips: Flip[] = [];
  for (const key of after.keyOrder) {
    const now = after.byKey.get(key)!;
    const was = before?.byKey.get(key);
    // A position that was on screen and changed is a correction; one new to the window is just read.
    const cause: FlipCause = was ? { kind: 'correction' } : { kind: 'snapshot' };
    const planes = new Set<PlaneId>([...now.planes.keys(), ...(was?.planes.keys() ?? [])]);
    for (const plane of planes) {
      const a = was?.planes.get(plane) ?? 0n;
      const b = now.planes.get(plane) ?? 0n;
      if (a === b) continue;
      flips.push({ world, key, plane, set: b & ~a, clear: a & ~b, cause });
    }
  }
  return flips;
};

export const createPlaneModel = (): PlaneModel => {
  const worlds = new Map<string, WorldMatter>();
  /** An occupier's kind never changes, so kinds are kept across snapshots. */
  const kindsOf = (world: string): Map<number, OccupierKind> => {
    const m = worlds.get(world);
    return m ? (m.kinds as Map<number, OccupierKind>) : new Map();
  };
  const globals = new Map<string, string>();

  return {
    snapshot(world, answer, window) {
      const before = worlds.get(world);
      // Kinds outlive the window: the snapshot names those it lists, earlier reads the rest.
      const after = matterFrom(world, globals.get(world) ?? before?.global ?? world, answer, window, new Map(kindsOf(world)));
      worlds.set(world, after);
      return diff(world, before, after);
    },

    apply(world, commits) {
      const m = worlds.get(world);
      if (!m) return { gap: true };
      const fresh = commits.filter((c) => c.seq > m.seq).sort((a, b) => a.seq - b.seq);
      if (fresh.length === 0) return { flips: [], seq: m.seq };
      // Judge the whole chain before touching anything: all of it applies, or none.
      let seq = m.seq;
      let head = m.head;
      for (const c of fresh) {
        if (c.seq !== seq + 1) return { gap: true };
        if (c.prevHash !== head) return { broken: true };
        seq = c.seq;
        head = c.hash;
      }
      const flips = fresh.flatMap((c) => forward(m, c));
      return { flips, seq: m.seq };
    },

    asOf(world, seq, older) {
      const m = worlds.get(world);
      if (!m) throw new Error(`no matter for ${world}`);
      const past = cloneMatter(m);
      if (seq >= m.seq) return past;
      const bySeq = new Map(older.map((c) => [c.seq, c]));
      for (let s = m.seq; s > seq; s -= 1) {
        const c = bySeq.get(s);
        if (!c) throw new Error(`commit ${s} is missing; read the journal since ${seq} first`);
        backward(past, c);
      }
      return past;
    },

    get: (world) => worlds.get(world),

    offeredOf(world, key) {
      const m = worlds.get(world);
      const pos = m?.byKey.get(key);
      if (!m || !pos) return 0n;
      return deriveOffered(pos.planes, m.kinds as Map<number, OccupierKind>, m.leafSlotBits);
    },

    name(world, global) {
      globals.set(world, global);
      const m = worlds.get(world);
      if (m && m.global !== global) worlds.set(world, { ...m, global });
    },

    learnKinds(world, occupiers) {
      const m = worlds.get(world);
      if (!m) return;
      const kinds = m.kinds as Map<number, OccupierKind>;
      let changed = false;
      for (const o of occupiers) {
        if (o.kind !== 'holder' && o.kind !== 'event' && o.kind !== 'series' && o.kind !== 'foreign') continue;
        if (kinds.get(o.id) !== o.kind) { kinds.set(o.id, o.kind); changed = true; }
      }
      if (!changed) return;
      for (const pos of m.byKey.values()) pos.offered = deriveOffered(pos.planes, kinds, m.leafSlotBits);
    },

    learnSeats(world, seats) {
      const m = worlds.get(world);
      if (m && Number.isInteger(seats) && seats > m.seats) m.seats = seats;
    },

    keyAt(world, parametric, coordinates) {
      const m = worlds.get(world);
      if (!m) return undefined;
      const wanted = positionKey(parametric, coordinates);
      for (const pos of m.byKey.values()) if (positionKey(pos.parametric, pos.coordinates) === wanted) return pos.key;
      return undefined;
    },

    forget(world) {
      worlds.delete(world);
    },
  };
};

export const createPlaneStore: CreatePlaneStore = createPlaneModel;
